import {
  Component,
  OnInit,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InfiniteScrollCustomEvent } from '@ionic/angular';
import { PAGINATION } from '@shared/constants/pagination.const';
import { ILoadDataOptions } from '@shared/interfaces';
import { Observable, Subject, EMPTY, catchError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

interface SearchRequest {
  source: string;
  activePage?: number;
}

@Component({
  template: '',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class InfiniteScrollBaseComponent<T> implements OnInit, OnChanges {
  @Input() searchValue = '';
  protected abstract loadMoreCount: number;
  protected abstract loadData(options: ILoadDataOptions): Observable<T[]>;

  public isLoading = true;
  public isLoadMore = false;
  public itemList: T[] = [];
  public activePage = 0;

  private readonly searchSubject = new Subject<SearchRequest>();
  protected readonly destroyRef = inject(DestroyRef);

  protected get LIST_UPDATE_SOURCE() {
    return PAGINATION.LIST_UPDATE_SOURCE;
  }

  constructor(protected cdr: ChangeDetectorRef) {
    this.initSearchPipeline();
  }

  private initSearchPipeline(): void {
    this.searchSubject
      .pipe(
        switchMap((request) => {
          const { source, activePage } = request;
          const { options, page } = this.buildSearchOptions(source, activePage);

          return this.loadData(options).pipe(
            catchError((error) => {
              this.onSearchError(error);
              return EMPTY;
            }),
            switchMap((data) => {
              this.processSearchResult(data, source, options, page);
              return EMPTY;
            })
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private buildSearchOptions(source: string, activePage?: number): { options: ILoadDataOptions; page: number } {
    let page = activePage ?? this.activePage;
    switch (source) {
      case this.LIST_UPDATE_SOURCE.MORE:
        page += 1;
        break;
      case this.LIST_UPDATE_SOURCE.UPDATE:
        break;
      default:
        page = 0;
        this.itemList = [];
        break;
    }

    const options: ILoadDataOptions = {
      start: page * this.loadMoreCount,
      end: this.loadMoreCount,
      page,
      searchValue: this.searchValue,
    };

    return { options, page };
  }

  private processSearchResult(data: T[], source: string, options: ILoadDataOptions, page: number): void {
    const isLessData = !data.length || data.length < options.end;
    if (source === this.LIST_UPDATE_SOURCE.MORE && isLessData) {
      page -= 1;
    }

    if (source === this.LIST_UPDATE_SOURCE.UPDATE) {
      this.itemList.splice(options.start, data.length, ...data);
    } else {
      this.itemList = [...this.itemList, ...data];
      this.activePage = page;
    }
    this.isLoadMore = isLessData;
    this.isLoading = false;
    this.onSearchComplete();
    this.cdr.markForCheck();
  }

  protected onSearchComplete(): void {}

  protected onSearchError(error: unknown): void {
    this.isLoading = false;
    this.cdr.markForCheck();
  }

  ngOnInit() {
    this.search(this.LIST_UPDATE_SOURCE.INIT);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { searchValue } = changes;
    if (searchValue && !searchValue.isFirstChange()) {
      this.handleSearchValue(searchValue.currentValue);
    }
  }

  loadMore(event?: Event) {
    const target = (<InfiniteScrollCustomEvent>event)?.target;
    const { activePage } = this;
    this.search(this.LIST_UPDATE_SOURCE.MORE);
    target?.complete();
    this.isLoadMore = activePage === this.activePage;
    this.cdr.markForCheck();
  }

  handleSearchValue(value: string) {
    let source = this.LIST_UPDATE_SOURCE.INIT;
    if (value) {
      source = this.LIST_UPDATE_SOURCE.SEARCH;
    }
    this.isLoadMore = true;
    this.showLoader();
    this.search(source);
  }

  protected search(source: string, activePage?: number): void {
    this.searchSubject.next({ source, activePage });
  }

  protected updateItems(start: number, end: number): void {
    const options: ILoadDataOptions = {
      start,
      end,
      page: this.activePage,
      searchValue: this.searchValue,
    };
    this.loadData(options)
      .pipe(
        catchError((error) => {
          this.onSearchError(error);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((data) => {
        this.itemList.splice(start, end, ...data);
        this.itemList = [...this.itemList];
        this.onSearchComplete();
        this.cdr.markForCheck();
      });
  }

  protected showLoader() {
    this.isLoading = true;
    this.cdr.markForCheck();
  }

  protected resetInfiniteScroll() {
    this.isLoadMore = false;
    this.cdr.markForCheck();
  }
}
